import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertCircle,
  Users,
  Database,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsHealth() {
  const [selfTest, setSelfTest] = useState(null);
  const [rosterCheck, setRosterCheck] = useState(null);
  const [loadingSelf, setLoadingSelf] = useState(false);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [err, setErr] = useState("");

  const runSelfTest = async () => {
    setLoadingSelf(true);
    setErr("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/health/self-test`, null, {
        withCredentials: true,
      });
      setSelfTest(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Self-test failed");
    } finally {
      setLoadingSelf(false);
    }
  };

  const runRosterCheck = async () => {
    setLoadingRoster(true);
    setErr("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/health/roster-check`, {
        withCredentials: true,
      });
      setRosterCheck(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Roster check failed");
    } finally {
      setLoadingRoster(false);
    }
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
            <Activity className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Integrations Health</h1>
            <p className="text-slate-600 mt-1">
              Verify that every part of the app is wired correctly and admin actions reach students.
            </p>
          </div>
        </div>

        {err && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {/* Self-test */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                System Self-Test
              </CardTitle>
              <Button
                onClick={runSelfTest}
                disabled={loadingSelf}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingSelf ? "animate-spin" : ""}`} />
                {loadingSelf ? "Running..." : "Run Self-Test"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selfTest ? (
              <p className="text-slate-500 text-sm">
                Click <strong>Run Self-Test</strong> to verify content, entities, files, sessions, and roster-student linkage.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 pb-3 border-b">
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {selfTest.passed} passed
                  </Badge>
                  {selfTest.failed > 0 && (
                    <Badge className="bg-red-100 text-red-800">
                      <XCircle className="w-3 h-3 mr-1" />
                      {selfTest.failed} failed
                    </Badge>
                  )}
                  <span className="text-xs text-slate-500">
                    Ran by {selfTest.ran_by}
                  </span>
                </div>
                {selfTest.checks.map((c) => (
                  <div
                    key={c.name}
                    className={`flex items-start gap-3 p-2 rounded-lg ${
                      c.status === "pass" ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    {c.status === "pass" ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${c.status === "pass" ? "text-green-900" : "text-red-900"}`}>
                        {c.name}
                      </p>
                      {c.details && (
                        <p className="text-xs text-slate-600 mt-0.5 break-all">{c.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Roster check */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Roster ↔ Student Verification
              </CardTitle>
              <Button
                onClick={runRosterCheck}
                disabled={loadingRoster}
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingRoster ? "animate-spin" : ""}`} />
                {loadingRoster ? "Checking..." : "Run Check"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!rosterCheck ? (
              <p className="text-slate-500 text-sm">
                Verify every EA assignment on the DB is linked to a real student record. Orphaned entries won&apos;t show up on any student&apos;s dashboard.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Roster entries" value={rosterCheck.total_roster_entries} />
                  <Stat label="Students" value={rosterCheck.total_students} />
                  <Stat
                    label="Linked"
                    value={rosterCheck.linked}
                    ok
                  />
                  <Stat
                    label="Orphaned"
                    value={rosterCheck.orphaned_no_matching_student}
                    danger={rosterCheck.orphaned_no_matching_student > 0}
                  />
                </div>

                {rosterCheck.orphaned_no_matching_student > 0 && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="w-4 h-4 text-amber-700" />
                    <AlertDescription className="text-amber-900 text-sm">
                      <strong>{rosterCheck.orphaned_no_matching_student} orphaned entries</strong> — these have a student_id that doesn&apos;t match any Student record. The affected student won&apos;t see them on their dashboard.
                      <details className="mt-1 text-xs">
                        <summary className="cursor-pointer">Show up to 10 examples</summary>
                        <ul className="mt-1 list-disc list-inside">
                          {rosterCheck.orphan_examples.map((o, i) => (
                            <li key={i}>
                              ID {o.student_id} — {o.name} — {o.teacher} — {o.assignment}
                            </li>
                          ))}
                        </ul>
                      </details>
                      <p className="mt-2 text-xs">
                        <strong>Fix:</strong> upload the Student roster first (User Types → Students → Bulk Import), then re-upload the Failure Report.
                      </p>
                    </AlertDescription>
                  </Alert>
                )}

                {rosterCheck.missing_email_field > 0 && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertCircle className="w-4 h-4 text-blue-700" />
                    <AlertDescription className="text-blue-900 text-sm">
                      <strong>{rosterCheck.missing_email_field} entries missing student_email</strong> — this used to hide entries from students, but the app now falls back to matching by student_id so this is only informational.
                    </AlertDescription>
                  </Alert>
                )}

                {rosterCheck.orphaned_no_matching_student === 0 && rosterCheck.total_roster_entries > 0 && (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle2 className="w-4 h-4 text-green-700" />
                    <AlertDescription className="text-green-900 text-sm">
                      Every roster entry is properly linked to a Student. All students will see their assignments.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, ok, danger }) {
  const color = danger ? "text-red-700 bg-red-50 border-red-200" : ok ? "text-green-700 bg-green-50 border-green-200" : "text-slate-800 bg-white border-slate-200";
  return (
    <div className={`rounded-lg border p-3 ${color}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
